import { useState } from "react";
import {
  Button,
  Card,
  Modal,
  Select,
  selectState as ss,
  TextInput,
  TimePicker,
  toast,
} from "@bfc-software/pallet";
import type { SelectMsg } from "@bfc-software/pallet";
import { match } from "ts-pattern";
// eslint-disable-next-line no-restricted-imports -- follow-up: use useCurrentLocalization()
import { TFunction } from "i18next";
import { logger } from "@bfc-software/app-toolbox";
import { Time } from "@internationalized/date";

import {
  useCreateShift,
  useDeleteShift,
  useShifts,
  useUpdateShift,
  useUpdateWarehouseTimezone,
  type Shift,
} from "../-data/warehouse-settings-client";
import {
  endMinuteAfter,
  formatClock,
  formatDuration,
  minuteToTimeValue,
  timeValueToMinute,
} from "../-data/shift-time";
import {
  filterTimezoneOptions,
  TIMEZONE_OPTIONS,
  type TimezoneOption,
} from "../-data/timezones";
import styles from "./shifts-card.module.css";

type ShiftFormValues = { name: string; start: Time; end: Time };

function ShiftFormModal({
  editing,
  isSubmitting,
  onSubmit,
  onClose,
  t,
}: {
  /** `undefined` opens the form empty (create); a `Shift` pre-fills it (edit). */
  editing: Shift | undefined | "new";
  isSubmitting: boolean;
  onSubmit: (values: ShiftFormValues) => Promise<void>;
  onClose: () => void;
  t: TFunction;
}) {
  const isOpen = editing !== undefined;
  const isEdit = editing !== undefined && editing !== "new";

  const [name, setName] = useState("");
  const [start, setStart] = useState<Time>(new Time(9, 0));
  const [end, setEnd] = useState<Time>(new Time(17, 0));

  // Re-seeds the form's local state whenever a different target opens --
  // `Modal`'s `control: "external"` means this component never unmounts
  // between opens, so a `useState` initializer alone would only run once.
  const [openedFor, setOpenedFor] = useState<typeof editing>(undefined);
  if (editing !== openedFor) {
    setOpenedFor(editing);
    if (isEdit) {
      setName(editing.name);
      setStart(minuteToTimeValue(editing.startMinute));
      setEnd(minuteToTimeValue(editing.endMinute));
    } else {
      setName("");
      setStart(new Time(9, 0));
      setEnd(new Time(17, 0));
    }
  }

  const submit = async () => {
    if (name.trim().length === 0) return;
    try {
      await onSubmit({ name: name.trim(), start, end });
      onClose();
    } catch {
      // onSubmit already reported the failure via toast; keep the modal open.
    }
  };

  return (
    <Modal
      control="external"
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      isDismissable={!isSubmitting}
      data-test-role="shift-form-modal"
      header={
        <h2>
          {isEdit
            ? t("systemControls.shifts.editTitle")
            : t("systemControls.shifts.addTitle")}
        </h2>
      }
      footer={
        <>
          <Button
            kind="text-only"
            variant="tertiary"
            text={t("systemControls.shifts.cancel")}
            onPress={onClose}
            isDisabled={isSubmitting}
          />
          <Button
            kind="text-only"
            variant="primary"
            text={
              isSubmitting
                ? t("systemControls.shifts.saving")
                : t("systemControls.shifts.save")
            }
            onPress={submit}
            isDisabled={isSubmitting || name.trim().length === 0}
          />
        </>
      }
    >
      <div className={styles.form}>
        <TextInput
          name="shift-name"
          label={t("systemControls.shifts.nameLabel")}
          value={name}
          onChange={(ev) => setName(ev.target.value)}
          placeholder={t("systemControls.shifts.namePlaceholder")}
          autofocus={true}
          isRequired={true}
        />
        <div className={styles.timeRow}>
          <TimePicker
            label={t("systemControls.shifts.startLabel")}
            value={start}
            onChange={(value) => value && setStart(value as Time)}
          />
          <TimePicker
            label={t("systemControls.shifts.endLabel")}
            value={end}
            onChange={(value) => value && setEnd(value as Time)}
          />
        </div>
      </div>
    </Modal>
  );
}

function DeleteShiftConfirm({
  shift,
  isPending,
  onCancel,
  onConfirm,
  t,
}: {
  shift: Shift | undefined;
  isPending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  t: TFunction;
}) {
  return (
    <Modal
      control="external"
      isOpen={shift !== undefined}
      isDismissable={!isPending}
      onOpenChange={(open) => {
        if (!open) onCancel();
      }}
      data-test-role="delete-shift-confirm"
      header={<h2>{t("systemControls.shifts.deleteConfirm.title")}</h2>}
      footer={
        <>
          <Button
            kind="text-only"
            variant="tertiary"
            text={t("systemControls.shifts.deleteConfirm.cancel")}
            onPress={onCancel}
            isDisabled={isPending}
          />
          <Button
            kind="text-only"
            variant="danger"
            text={t("systemControls.shifts.deleteConfirm.confirm")}
            onPress={onConfirm}
            isDisabled={isPending}
          />
        </>
      }
    >
      <p>{t("systemControls.shifts.deleteConfirm.body")}</p>
      {shift ? <p className={styles.deleteTarget}>{shift.name}</p> : null}
    </Modal>
  );
}

/**
 * The warehouse's IANA zone, as pallet's `Select` (`always`: a zone is always
 * set, so there is no clear) with search over every zone the runtime knows
 * about (`TIMEZONE_OPTIONS`): a set this large (~400 zones) is one people find
 * by typing, not by scrolling a scannable list. The search matches with
 * `filterTimezoneOptions` (label or id), narrowed here since pallet never
 * filters. The popover opens onto the full catalog, and the search text resets
 * when it closes.
 */
function WarehouseTimezoneField({
  orgId,
  warehouseId,
  timezone,
  timezoneLabel,
  t,
}: {
  orgId: string;
  warehouseId: string | null;
  timezone: string;
  timezoneLabel: string;
  t: TFunction;
}) {
  const updateTimezone = useUpdateWarehouseTimezone(orgId, warehouseId);
  const [text, setText] = useState("");

  const state = ss.withSearch(
    ss.always({
      label: ss.ariaLabel(t("systemControls.shifts.timezoneLabel")),
      options: ss.ready({
        items: filterTimezoneOptions(TIMEZONE_OPTIONS, text),
        emptyLabel: t("systemControls.shifts.timezoneEmpty"),
      }),
      selected: { id: timezone, label: timezoneLabel },
    }),
    {
      placeholder: t("systemControls.shifts.timezonePlaceholder"),
      text,
    },
  );

  const onMsg = (msg: SelectMsg<TimezoneOption>) =>
    match(msg)
      .with({ kind: "searched" }, ({ text }) => setText(text))
      // Must be explicit: the `.otherwise` below would swallow it.
      .with({ kind: "closed" }, () => setText(""))
      .with({ kind: "picked" }, ({ item }) => {
        if (item.id === timezone) return;
        updateTimezone.mutate(item.id, {
          onSuccess: () =>
            logger.info("Updated warehouse timezone", { timezone: item.id }),
          onError: (error) => {
            logger.warn("Failed to update warehouse timezone", {
              error,
              timezone: item.id,
            });
            toast.error(t("systemControls.shifts.timezoneSaveFailed"));
          },
        });
      })
      // `pickedN` / `cleared` cannot arrive from `always`; `opened` is unused.
      .otherwise(() => {});

  return (
    <div className={styles.timezone}>
      <span className={styles.timezoneLabel}>
        {t("systemControls.shifts.timezoneLabel")}
      </span>
      <Select
        state={state}
        onMsg={onMsg}
        className={styles.timezoneCombo}
        data-testid="warehouse-timezone-select"
      />
    </div>
  );
}

export function ShiftsCard({
  orgId,
  warehouseId,
  t,
}: {
  orgId: string;
  warehouseId: string | null;
  t: TFunction;
}) {
  const shiftsQuery = useShifts(orgId, warehouseId);
  const createShift = useCreateShift(orgId, warehouseId);
  const updateShift = useUpdateShift(orgId, warehouseId);
  const deleteShift = useDeleteShift(orgId, warehouseId);

  const [isEditing, setIsEditing] = useState(false);
  const [formTarget, setFormTarget] = useState<Shift | "new" | undefined>();
  const [deleteTarget, setDeleteTarget] = useState<Shift | undefined>();

  const shifts = shiftsQuery.data?.shifts ?? [];

  const submitForm = async (values: ShiftFormValues) => {
    const startMinute = timeValueToMinute(values.start);
    const endMinute = endMinuteAfter(
      startMinute,
      timeValueToMinute(values.end),
    );
    const input = { name: values.name, startMinute, endMinute };

    try {
      if (formTarget && formTarget !== "new") {
        await updateShift.mutateAsync({ id: formTarget.id, ...input });
        logger.info("Updated shift", { id: formTarget.id });
      } else {
        await createShift.mutateAsync(input);
        logger.info("Created shift", { name: input.name });
      }
    } catch (error) {
      logger.warn("Failed to save shift", { error });
      toast.error(t("systemControls.shifts.saveFailed"));
      throw error;
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteShift.mutateAsync(deleteTarget.id);
      logger.info("Deleted shift", { id: deleteTarget.id });
      setDeleteTarget(undefined);
    } catch (error) {
      logger.warn("Failed to delete shift", { error, id: deleteTarget.id });
      toast.error(t("systemControls.shifts.deleteConfirm.errorToast"));
    }
  };

  return (
    <>
      <Card
        variant="panel"
        layout="body-only"
        data-test-role="shifts-card"
        body={
          <>
            <div className={styles.header}>
              <div>
                <div className={styles.title}>
                  {t("systemControls.shifts.title")}
                </div>
                <div className={styles.subtitle}>
                  {t("systemControls.shifts.subtitle")}
                </div>
              </div>
              <div className={styles.headerActions}>
                <Button
                  kind="text-icon-left"
                  variant="secondary"
                  icon="Plus"
                  text={t("systemControls.shifts.addButton")}
                  onPress={() => setFormTarget("new")}
                />
                <Button
                  kind="icon-only"
                  variant={isEditing ? "filled" : "tertiary"}
                  icon={isEditing ? "Check" : "Edit2"}
                  label={
                    isEditing
                      ? t("systemControls.shifts.doneEditing")
                      : t("systemControls.shifts.edit")
                  }
                  onPress={() => setIsEditing((prev) => !prev)}
                />
              </div>
            </div>

            {shiftsQuery.data ? (
              <WarehouseTimezoneField
                orgId={orgId}
                warehouseId={warehouseId}
                timezone={shiftsQuery.data.warehouseTimezone}
                timezoneLabel={shiftsQuery.data.warehouseTimezoneLabel}
                t={t}
              />
            ) : null}

            <div className={styles.list}>
              {shifts.map((shift) => (
                <div
                  key={shift.id}
                  className={styles.row}
                  data-test-role="shift-row"
                >
                  <span className={styles.name}>{shift.name}</span>
                  <span className={styles.time}>
                    {formatClock(shift.startMinute)} –{" "}
                    {formatClock(shift.endMinute)}
                  </span>
                  <span className={styles.duration}>
                    {formatDuration(shift.startMinute, shift.endMinute)}
                  </span>
                  {isEditing ? (
                    <div className={styles.rowActions}>
                      <Button
                        kind="icon-only"
                        variant="tertiary"
                        icon="Edit2"
                        label={t("systemControls.shifts.editButton", {
                          name: shift.name,
                        })}
                        onPress={() => setFormTarget(shift)}
                      />
                      <Button
                        kind="icon-only"
                        variant="tertiary"
                        icon="Trash"
                        label={t("systemControls.shifts.deleteButton", {
                          name: shift.name,
                        })}
                        onPress={() => setDeleteTarget(shift)}
                      />
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          </>
        }
      />
      <ShiftFormModal
        editing={formTarget}
        isSubmitting={createShift.isPending || updateShift.isPending}
        onSubmit={submitForm}
        onClose={() => setFormTarget(undefined)}
        t={t}
      />
      <DeleteShiftConfirm
        shift={deleteTarget}
        isPending={deleteShift.isPending}
        onCancel={() => setDeleteTarget(undefined)}
        onConfirm={confirmDelete}
        t={t}
      />
    </>
  );
}
